import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { blogData } from "../Blog/BlogData"; 
import {
  Calendar,
  User,
  Facebook,
  Twitter,
  Linkedin,
  ArrowLeft,
  Scale,
  MessageSquare,
} from "lucide-react";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import { Helmet } from "react-helmet";

// COLORS declared at the top as requested
const COLORS = {
  bgPrimary: "#0B1C2D",   // Deep Navy
  bgSecondary: "#13293D", // Slightly Lighter Navy
  gold: "#C6A75E",        // Premium Gold
  textLight: "#EAEAEA",
};

const BlogDetails = () => {
  const { blogId } = useParams();
  const blog = blogData.find((b) => b.id === blogId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [blogId]);

  if (!blog) {
    return (
      <div className="py-40 text-center font-serif" style={{ backgroundColor: COLORS.bgPrimary, color: COLORS.textLight }}>
        <h2 className="text-3xl mb-4">Article Not Found</h2>
        <Link to="/blogs" style={{ color: COLORS.gold }} className="underline">
          Back to Insights
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} | Nyaysakti Advocates & Solicitors</title>
        <meta name="description" content={blog.excerpt} />
        <meta name="author" content={blog.author} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:image" content={blog.image} />
      </Helmet>
      
      <Header />

      <div className="pt-32 pb-20 overflow-hidden min-h-screen" style={{ backgroundColor: COLORS.bgPrimary }}>
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Back Navigation */}
          <Link
            to="/blogs"
            className="group inline-flex items-center gap-2 mb-10 font-sans uppercase text-[10px] font-bold tracking-[3px] transition-all"
            style={{ color: COLORS.gold }}
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-2 transition-transform" />
            Back to Legal Insights
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* LEFT COLUMN: Main Article */}
            <div className="lg:col-span-8">
              <header className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <span 
                    className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-[3px]"
                    style={{ backgroundColor: COLORS.gold, color: COLORS.bgPrimary }}
                  >
                    {blog.category}
                  </span>
                  <div className="h-[1px] w-12 opacity-30" style={{ backgroundColor: COLORS.gold }}></div>
                </div>

                <h1 className="text-white text-4xl md:text-6xl font-bold leading-[1.1] mb-10">
                  {blog.title}
                </h1>

                <div className="flex flex-wrap items-center gap-8 text-gray-400 text-[11px] border-y border-white/10 py-8 font-sans uppercase tracking-[2px]">
                  <span className="flex items-center gap-2">
                    <Calendar size={14} style={{ color: COLORS.gold }} /> {blog.date}
                  </span>
                  <span className="flex items-center gap-2 text-white font-bold">
                    <User size={14} style={{ color: COLORS.gold }} /> By {blog.author}
                  </span>
                </div>
              </header>

              {/* Featured Image with Premium Border Detail */}
              <div className="relative mb-16">
                <div 
                    className="absolute inset-0 border-[1px] translate-x-4 translate-y-4 -z-10 opacity-20"
                    style={{ borderColor: COLORS.gold }}
                ></div>
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-[550px] object-cover rounded-xl shadow-2xl"
                />
                <div className="absolute -bottom-10 -right-10 hidden md:block opacity-5">
                  <Scale size={200} color={COLORS.gold} />
                </div>
              </div>

              {/* Article Content */}
              <div className="legal-journal-content-container">
                <div
                  className="article-body-text text-gray-300"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              </div>

              {/* Social Share */}
              <div className="mt-24 pt-10 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <p className="text-white text-xl font-bold mb-2">Nyaysakti Advocates</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-[2px]">Ensuring Justice Through Expertise</p>
                </div>
                <div className="flex gap-6">
                  {[Facebook, Twitter, Linkedin].map((Icon, idx) => (
                      <Icon key={idx} size={20} className="text-gray-400 hover:text-white transition-all cursor-pointer" />
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Sidebar */}
            <div className="lg:col-span-4">
              <div className="sticky top-32 space-y-8">
                
                {/* Consultation Card */}
                <div 
                    className="p-10 text-white rounded-2xl shadow-2xl overflow-hidden relative border border-white/5"
                    style={{ backgroundColor: COLORS.bgSecondary }}
                >
                  <div 
                    className="absolute top-0 right-0 w-32 h-32 rounded-full -mr-16 -mt-16 opacity-10"
                    style={{ backgroundColor: COLORS.gold }}
                  ></div>

                  <h3 className="text-2xl font-bold mb-6 border-b pb-4 relative z-10" style={{ color: COLORS.gold, borderColor: "rgba(206, 167, 94, 0.2)" }}>
                    Legal Assistance
                  </h3>
                  <p className="text-sm text-gray-400 font-sans mb-10 leading-[1.8] italic relative z-10">
                    "The law is a profession of words; we ensure your words carry the weight of justice."
                  </p>

                  <Link to="/contact-us" className="relative z-10 block">
                    <button 
                        className="w-full bg-transparent border-2 font-bold py-5 uppercase text-[10px] tracking-[4px] transition-all duration-500 rounded-xl hover:text-white"
                        style={{ borderColor: COLORS.gold, color:"#fff" }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.gold}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                    >
                      Request Consultation
                    </button>
                  </Link>

                  <div className="mt-12 pt-8 border-t border-white/5 flex flex-col gap-4 text-[11px] font-sans tracking-[1px] text-gray-500">
                    <p className="flex items-center gap-2"><MessageSquare size={14} style={{ color: COLORS.gold }} /> Confidential Advisory</p>
                    <p>📍 Saket Court, New Delhi</p>
                  </div>
                </div>

                {/* Topics Cloud */}
                <div className="p-8 rounded-2xl border border-white/5" style={{ backgroundColor: COLORS.bgSecondary }}>
                  <h4 className="font-bold text-white mb-6 text-xs uppercase tracking-widest">
                    Legal Topics
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {["Family Law", "Criminal Law", "Bail", "Divorce", "RERA"].map((tag) => (
                      <span
                        key={tag}
                        className="text-[9px] border px-4 py-2 uppercase text-gray-400 hover:text-white cursor-pointer transition-all rounded-full"
                        style={{ borderColor: "rgba(255,255,255,0.1)" }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Journal Styling */}
        <style dangerouslySetInnerHTML={{ __html: `
            .article-body-text {
              font-family: 'Inter', sans-serif;
              line-height: 2;
              font-size: 1.125rem;
            }
            .article-body-text p {
              margin-bottom: 2rem;
              opacity: 0.85;
            }
            .article-body-text h2 {
              font-size: 2.5rem;
              color: white;
              font-weight: 700;
              margin: 4rem 0 1.5rem;
              border-left: 4px solid ${COLORS.gold};
              padding-left: 20px;
            }
            .article-body-text strong {
              color: ${COLORS.gold};
              font-weight: 700;
            }
            .article-body-text blockquote {
              margin: 3rem 0;
              padding: 2.5rem;
              background: ${COLORS.bgSecondary};
              border-radius: 1rem;
              border-left: 4px solid ${COLORS.gold};
              font-style: italic;
              font-size: 1.4rem;
              color: white;
            }
            .article-body-text ul {
              margin-bottom: 2rem;
            }
            .article-body-text li {
              position: relative;
              padding-left: 1.5rem;
              margin-bottom: 1rem;
              list-style: none;
            }
            .article-body-text li::before {
              content: "";
              position: absolute;
              left: 0;
              top: 12px;
              width: 8px;
              height: 2px;
              background: ${COLORS.gold};
            }
        `}} />
      </div>

      <Footer />
    </>
  );
};

export default BlogDetails;